import type { EntityId, PositionView } from '@stockstop/shared';
import { useRef, useState } from 'react';
import { useReorderPositions } from '../../api/queries.js';

/**
 * Umsortieren des Portfolios per Griff.
 *
 * Ohne Bibliothek: Gezogen wird der Griff, nicht die Karte. Wäre die Karte selbst
 * `draggable`, ließe sich in ihr kein Text mehr markieren — und auf einer Karte
 * voller Beträge will man gelegentlich eine Zahl herauskopieren. Über
 * `setDragImage` wird trotzdem die ganze Karte am Zeiger gezeigt und nicht nur
 * das Griffsymbol.
 *
 * Die Tastatur bedient dieselbe Bewegung ohne Ziehen: Pfeil hoch und runter auf
 * dem Griff verschieben die Karte um einen Platz. Natives Ziehen ist mit der
 * Tastatur nicht erreichbar, und eine Reihenfolge, die sich nur mit der Maus
 * ändern lässt, wäre für einen Teil der Bedienung schlicht nicht vorhanden.
 */

export interface CardReorder {
  /** Die Karte, die gerade gezogen wird. */
  draggingId: EntityId | null;
  /** Die Karte, über der sie gerade schwebt. */
  overId: EntityId | null;
  /** Wird die Karte über ihrer Oberkante oder Unterkante abgelegt? */
  dropBefore: boolean;
  isPending: boolean;
  /** Meldet die Karte an, damit sie beim Ziehen am Zeiger hängt. */
  registerCard: (position: PositionView) => (element: HTMLElement | null) => void;
  handleProps: (position: PositionView) => {
    draggable: true;
    onDragStart: (event: React.DragEvent) => void;
    onDragEnd: () => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
  };
  cardProps: (position: PositionView) => {
    onDragOver: (event: React.DragEvent) => void;
    onDrop: (event: React.DragEvent) => void;
  };
}

export const useCardReorder = (positions: PositionView[]): CardReorder => {
  const reorder = useReorderPositions();
  const [draggingId, setDraggingId] = useState<EntityId | null>(null);
  const [overId, setOverId] = useState<EntityId | null>(null);
  const [dropBefore, setDropBefore] = useState(true);

  /* Die Karte zum Griff — für `setDragImage`, damit die ganze Karte am Zeiger
   * hängt und nicht das Griffsymbol allein. */
  const cardOf = useRef(new Map<number, HTMLElement>()).current;

  const ids = positions.map((position) => position.id);

  const commit = (next: EntityId[]): void => {
    /* Nichts bewegt, nichts zu speichern: Ein Aufruf ohne Wirkung würde die Liste
     * neu laden und dabei kurz flackern. */
    if (next.length === ids.length && next.every((id, index) => id === ids[index])) return;
    reorder.mutate(next);
  };

  const move = (id: EntityId, delta: number): void => {
    const from = ids.indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;

    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, id);
    commit(next);
  };

  const drop = (targetId: EntityId): void => {
    if (draggingId === null || draggingId === targetId) return;

    const next = ids.filter((id) => id !== draggingId);
    const index = next.indexOf(targetId);
    if (index < 0) return;

    next.splice(dropBefore ? index : index + 1, 0, draggingId);
    commit(next);
  };

  return {
    draggingId,
    overId,
    dropBefore,
    isPending: reorder.isPending,

    registerCard: (position) => (element) => {
      if (element) cardOf.set(position.id, element);
      else cardOf.delete(position.id);
    },

    handleProps: (position) => ({
      draggable: true,
      onDragStart: (event) => {
        event.stopPropagation();
        setDraggingId(position.id);
        event.dataTransfer.effectAllowed = 'move';
        /* Ohne Nutzlast bricht Firefox das Ziehen sofort wieder ab. */
        event.dataTransfer.setData('text/plain', String(position.id));

        const card = cardOf.get(position.id);
        if (card) {
          const box = card.getBoundingClientRect();
          event.dataTransfer.setDragImage(card, event.clientX - box.left, event.clientY - box.top);
        }
      },
      onDragEnd: () => {
        setDraggingId(null);
        setOverId(null);
      },
      onKeyDown: (event) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
        /* Sonst rollt die Seite, während die Karte wandert. */
        event.preventDefault();
        event.stopPropagation();
        move(position.id, event.key === 'ArrowUp' ? -1 : 1);
      },
    }),

    cardProps: (position) => ({
      onDragOver: (event) => {
        if (draggingId === null) return;
        /* Ohne `preventDefault` lehnt der Browser das Ablegen ab. */
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        const box = event.currentTarget.getBoundingClientRect();
        setOverId(position.id);
        setDropBefore(event.clientY < box.top + box.height / 2);
      },
      onDrop: (event) => {
        event.preventDefault();
        drop(position.id);
        setDraggingId(null);
        setOverId(null);
      },
    }),
  };
};
